// Map type_document to typeKey for translation
const TYPE_DOCUMENT_TO_TYPEKEY = {
  BILAN_BIOCHIMIQUE: "BILAN_BIOCHIMIQUE",
  IRM: "IRM",
  ECHOGRAPHIE: "ECHOGRAPHIE",
  RADIOGRAPHIE: "RADIOGRAPHIE",
  ORDONNANCE: "ORDONNANCE",
  COMPTE_RENDU: "COMPTE_RENDU",
  AUTRE: "AUTRE",
  // Ajoute ici d'autres correspondances si besoin
};

function getTypeKey(record) {
  return record?.typeKey || TYPE_DOCUMENT_TO_TYPEKEY[record?.type_document] || undefined;
}
// src/pages/.../RecordListPanel.jsx
"use client";
import React from "react";
import {
  Button,
  Space,
  Dropdown,
  DatePicker,
  Input,
  List,
  Tooltip,
  Popconfirm,
  Empty,
  Spin,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  FilterOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { moveDocumentToTrash } from "../../redux/thunks/trashThunks";
import "./RecordListPanel.css";
import Cookies from "js-cookie";
const { RangePicker } = DatePicker;

export default function RecordListPanel({
  selectedRecord,
  items = [],
  loading = false,
  onItemClick,
  onDelete,
  onBack,
  isDoctor: isDoctorProp,
  viewMode,
  subjectPatientId = null,
  canArchive = false,
}) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  // Direction du texte selon la langue active (ar => rtl),
  // même logique que dans les autres composants.
  const isRTL = i18n.dir(i18n.language) === "rtl";

  // ✅ Auto-détection du rôle si non fourni explicitement
  const isDoctorDetected = React.useMemo(() => {
    if (isDoctorProp !== undefined) return isDoctorProp;
    if (viewMode === "doctor") return true;
    if (viewMode === "patient") return false;
    const path = location.pathname.toLowerCase();
    if (path.includes("/doctor")) return true;
    try {
      const user = JSON.parse(Cookies.get("user") || "{}");
      if (user?.role === "doctor" || user?.role === "DOCTOR") return true;
    } catch {}
    return false;
  }, [isDoctorProp, viewMode, location.pathname]);

  const hideDeleteAction = isDoctorDetected && !canArchive;

  // ✅ deletedIds : on garde juste les ids supprimés localement
  const [deletedIds, setDeletedIds] = React.useState(new Set());
  const [deletingId, setDeletingId] = React.useState(null);

  // ✅ Filters
  const [sortKey, setSortKey] = React.useState("recent");
  const [period, setPeriod] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState("");

  // ✅ On filtre directement depuis items (prop) sans passer par un state local
  const filteredItems = React.useMemo(() => {
    // Exclure les items supprimés localement
    let result = (items || []).filter((item) => !deletedIds.has(item._id || item.id));

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((item) => {
        const fileName = (item.nom_fichier || "").toLowerCase();
        const docType = (item.type_document || "").toLowerCase();
        const doctor = (item.medecin_nom || item.doctor_name || "").toLowerCase();
        return (
          fileName.includes(query) ||
          docType.includes(query) ||
          doctor.includes(query)
        );
      });
    }

    if (period && period[0] && period[1]) {
      result = result.filter((item) => {
        const itemDate = new Date(item.date_upload || item.createdAt);
        return itemDate >= period[0].toDate() && itemDate <= period[1].toDate();
      });
    }

    if (sortKey === "recent") {
      result.sort((a, b) => new Date(b.date_upload || b.createdAt || 0) - new Date(a.date_upload || a.createdAt || 0));
    } else if (sortKey === "oldest") {
      result.sort((a, b) => new Date(a.date_upload || a.createdAt || 0) - new Date(b.date_upload || b.createdAt || 0));
    } else if (sortKey === "name") {
      result.sort((a, b) => (a.nom_fichier || "").toLowerCase().localeCompare((b.nom_fichier || "").toLowerCase()));
    }

    return result;
  }, [items, deletedIds, searchQuery, period, sortKey]);

  const sortMenuItems = React.useMemo(
    () => [
      { key: "recent", label: t("medical.list.sort.recent", "Most recent") },
      { key: "oldest", label: t("medical.list.sort.oldest", "Oldest first") },
      { key: "name", label: t("medical.list.sort.name", "Name (A-Z)") },
    ],
    [t]
  );

  const handleDeleteConfirm = async (e, item) => {
    e?.stopPropagation();

    if (hideDeleteAction) {
      message.error(t("medical.list.errors.doctorCannotDelete", "Doctors cannot delete patient documents"));
      return;
    }

    const documentId = item._id || item.id;
    const collection = item.collection || selectedRecord?.collection;

    if (!documentId || !collection) {
      message.error(t("medical.list.errors.missingData", "Missing document information"));
      return;
    }

    setDeletingId(documentId);

    try {
      const result = await dispatch(
        moveDocumentToTrash({
          collection,
          documentId,
          userId: subjectPatientId,
          version: item?.version,
        }),
      ).unwrap();

      // ✅ Marquer comme supprimé localement sans toucher items
      setDeletedIds((prev) => new Set([...prev, documentId]));

      message.success(t("medical.list.success.moveToTrash", "Document moved to trash."));

      onDelete?.({
        id: documentId,
        _id: item._id,
        type: item.type_document,
        collection,
        rawData: item,
        result,
      });
    } catch (error) {
      const fallbackMessage = t("medical.list.errors.moveToTrash", "Failed to move document to trash");
      message.error(typeof error === "string" ? error : error?.message || fallbackMessage);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteCancel = (e) => e?.stopPropagation();

  const handleBackClick = (e) => {
    e?.stopPropagation();
    onBack?.();
  };

  return (
    <div className="rtp-left card" dir={isRTL ? "rtl" : "ltr"}>
      <div className="rtp-left-topline">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={handleBackClick}
          className="rtp-back-btn"
        >
          {t("medical.list.actions.previous", "Previous")}
        </Button>
      </div>

      <div className="rtp-left-title">
        <div className="rtp-left-docicon" aria-hidden="true">
          {selectedRecord?.icon || "📄"}
        </div>
        <div>
          <h2 className="rtp-left-heading">
            {getTypeKey(selectedRecord)
              ? t(`medicalHistory.types.${getTypeKey(selectedRecord)}.title`, selectedRecord?.label || t("medical.list.defaults.title", "Documents"))
              : (selectedRecord?.label || t("medical.list.defaults.title", "Documents"))}
          </h2>
          <div className="rtp-left-sub">
            {getTypeKey(selectedRecord)
              ? t(`medicalHistory.types.${getTypeKey(selectedRecord)}.desc`, selectedRecord?.description || t("medical.list.defaults.description", "Medical documents and analysis results"))
              : (selectedRecord?.description || t("medical.list.defaults.description", "Medical documents and analysis results"))}
          </div>
        </div>
      </div>

      <div className="rtp-left-filters">
        <Space wrap>
          <Dropdown
            trigger={["click"]}
            menu={{
              items: sortMenuItems,
              selectable: true,
              selectedKeys: [sortKey],
              onClick: ({ key }) => setSortKey(key),
            }}
          >
            <Button icon={<FilterOutlined />}>
              {sortMenuItems.find((it) => it.key === sortKey)?.label ||
                t("medical.list.sort.button", "Sort")}
            </Button>
          </Dropdown>

          <RangePicker
            value={period}
            onChange={(dates) => setPeriod(dates)}
            allowEmpty={[true, true]}
            placeholder={[
              t("medical.list.filters.startDate", "Start date"),
              t("medical.list.filters.endDate", "End date"),
            ]}
            style={{ display: "none" }}
          />

          <Input.Search
            placeholder={t("medical.list.filters.searchPlaceholder", "Search documents...")}
            value={searchQuery}
            allowClear
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: 260, display: "none" }}
          />
        </Space>
      </div>

      <div className="rtp-left-listwrap">
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Spin size="large" tip={t("medical.list.loading", "Loading documents...")} />
          </div>
        ) : filteredItems.length === 0 ? (
          <Empty
            description={
              searchQuery || period
                ? t("medical.list.empty.filtered", "No documents match your filters")
                : t("medical.list.empty.default", "No documents found")
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: "60px 0" }}
          />
        ) : (
          <List
            className="rtp-list"
            dataSource={filteredItems}
            rowKey={(item) => item._id || item.id || item.nom_fichier}
            split={false}
            renderItem={(item) => {
              const itemDate = new Date(item.date_upload || item.createdAt || Date.now());
              const day = itemDate.getDate();
              const month = itemDate.toLocaleString(i18nLanguageSafe(i18n), { month: "short" });

              const fileName = item.nom_fichier || t("medical.list.defaults.unnamed", "Unnamed Document");
              const doctor =
                item.medecin_nom ||
                item.doctor_name ||
                t("medical.list.defaults.unknownDoctor", "Unknown Doctor");

              const docId = item._id || item.id;
              const isDeleting = deletingId === docId;

              return (
                <List.Item
                  className="rtp-item"
                  onClick={() => onItemClick?.(item)}
                  style={{ cursor: "pointer", opacity: isDeleting ? 0.5 : 1 }}
                >
                  <div className="rtp-date-pill">
                    <div className="day">{day}</div>
                    <div className="month">
                      {String(month).replace(/[.\s]/g, "").slice(0, 4)}
                    </div>
                  </div>

                  <div className="rtp-item-texts">
                    <div className="rtp-item-doctor">{doctor}</div>
                    <div className="rtp-item-title" title={fileName}>
                      {fileName}
                    </div>
                  </div>

                  <Space className="rtp-item-actions" onClick={(e) => e.stopPropagation()}>
                    {!hideDeleteAction && (
                      <Tooltip title={t("medical.list.actions.deleteTooltip", "Move to trash")}>
                        <Popconfirm
                          title={t("medical.list.confirmDelete.title", "Move to trash?")}
                          description={t(
                            "medical.list.confirmDelete.description",
                            "You can restore this document later from the trash."
                          )}
                          onConfirm={(e) => {
                            e?.domEvent?.stopPropagation?.();
                            handleDeleteConfirm(e, item);
                          }}
                          onCancel={handleDeleteCancel}
                          okText={t("medical.list.confirmDelete.ok", "Yes, move")}
                          cancelText={t("medical.list.confirmDelete.cancel", "Cancel")}
                          okButtonProps={{ danger: true, loading: isDeleting }}
                        >
                          <Button
                            danger
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            className="rtp-btn rtp-btn--del1"
                            loading={isDeleting}
                            disabled={isDeleting}
                          />
                        </Popconfirm>
                      </Tooltip>
                    )}

                    <Tooltip title={t("medical.list.actions.viewTooltip", "View document")}>
                      <Button
                        type="text"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onItemClick?.(item);
                        }}
                        className="rtp-btn rtp-btn--send"
                        disabled={isDeleting}
                      />
                    </Tooltip>
                  </Space>
                </List.Item>
              );
            }}
          />
        )}
      </div>
    </div>
  );
}

function i18nLanguageSafe(i18n) {
  try {
    const lang = localStorage.getItem("i18nextLng") || i18n?.language || "en";
    return String(lang).replace("_", "-");
  } catch {
    return "en";
  }
}